import React, { useState, useEffect } from "react";
import { Label } from "../../DemoClient/Components/label";
import { clientsData, binData } from "../../DemoClient/Data/mockData";
import { useParams } from "react-router-dom";
import { Input } from "../../DemoClient/Components/Input";
import { Button, TOAST, Switch } from "retro";
import { Lock, AlertTriangle } from "lucide-react";

const LimitSlider = ({ label, value, onChange, min = 0, max = 1000000, step = 1000 }) => (
    <div style={{ backgroundColor: "#FEF2F2", borderRadius: 8, border: "1px solid #FECACA", padding: 12 }}>
        <Label style={{ fontSize: 12, fontWeight: 600 }}>{label}</Label>

        <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value || 0}
            onChange={(e) => onChange(Number(e.target.value))}
            style={{ width: "100%", marginTop: 8 }}
        />

        <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4 }}>
            {Number(value || 0).toLocaleString()}
        </div>
    </div>
);


function CardLimits() {
    const { id } = useParams();
    const currentClient = clientsData.find((client) => client.id === parseInt(id));

    const mappedBINs = currentClient?.mappedBINs || [];
    const filteredBINs = binData.filter((bin) => mappedBINs.includes(bin.bin));

    const [selectedBIN, setSelectedBIN] = useState(filteredBINs[0]?.bin || "");

    const defaultAllowedTransactions = {
        POS: true,
        Online: false,
        ATM: true,
        Contactless: false,
    };

    const emiOptions = [
        { value: "3", label: "3 Months" },
        { value: "6", label: "6 Months" },
        { value: "9", label: "9 Months" },
        { value: "12", label: "12 Months" },
    ];

    const toggleEmiDuration = (value) => {
        setLimits((prev) => {
            const exists = prev.emiDuration.includes(value);

            return {
                ...prev,
                emiDuration: exists
                    ? prev.emiDuration.filter((v) => v !== value)
                    : [...prev.emiDuration, value],
            };
        });
    };

    const [limits, setLimits] = useState({
        maxCardLimit: filteredBINs[0]?.maxCardLimit || "",
        dailyLimit: filteredBINs[0]?.dailyLimit || "",
        monthlyLimit: filteredBINs[0]?.monthlyLimit || "",
        transactionLimit: filteredBINs[0]?.transactionLimit || "",
        txnPerDay: filteredBINs[0]?.txnPerDay || "",
        txnPerHour: filteredBINs[0]?.txnPerHour || "",
        posDailyLimit: filteredBINs[0]?.posDailyLimit || "",
        posMonthlyLimit: filteredBINs[0]?.posMonthlyLimit || "",
        onlineDailyLimit: filteredBINs[0]?.onlineDailyLimit || "",
        onlineMonthlyLimit: filteredBINs[0]?.onlineMonthlyLimit || "",
        contactlessDailyLimit: filteredBINs[0]?.contactlessDailyLimit || "",
        contactlessMonthlyLimit: filteredBINs[0]?.contactlessMonthlyLimit || "",
        atmDailyLimit: filteredBINs[0]?.atmDailyLimit || "",
        atmMonthlyLimit: filteredBINs[0]?.atmMonthlyLimit || "",
        atmTransactionLimit: filteredBINs[0]?.atmTransactionLimit || "",
        posTransactionLimit: filteredBINs[0]?.posTransactionLimit || "",
        onlineTransactionLimit: filteredBINs[0]?.onlineTransactionLimit || "",
        contactlessTransactionLimit: filteredBINs[0]?.contactlessTransactionLimit || "",
        expiryDate: "",
        allowedTransactions: { ...defaultAllowedTransactions },
        allowCNP: filteredBINs[0]?.allowCNP || false,
        cnpChannels: filteredBINs[0]?.cnpChannels || "Web",
        recurringPaymentsUpto: filteredBINs[0]?.recurringPaymentsUpto || "",
        emiDuration: filteredBINs[0]?.emiDuration || [],
        cardAdvance: filteredBINs[0]?.cardAdvance || false,
        cashAdvanceLimit: filteredBINs[0]?.cashAdvanceLimit || { type: "ABSOLUTE", value: 0 },
        finalCashAdvanceLimit: filteredBINs[0]?.finalCashAdvanceLimit || 0,
        cashAdvanceDailyCap: filteredBINs[0]?.cashAdvanceDailyCap || 0,
        cashAdvanceFee: filteredBINs[0]?.cashAdvanceFee || 0,
    });

    // Update limits when BIN selection changes
    const handleBINChange = (binValue) => {
        setSelectedBIN(binValue);
        const binDataSelected = filteredBINs.find((bin) => bin.bin === binValue);
        setLimits({
            maxCardLimit: binDataSelected?.maxCardLimit || "",
            dailyLimit: binDataSelected?.dailyLimit || "",
            monthlyLimit: binDataSelected?.monthlyLimit || "",
            transactionLimit: binDataSelected?.transactionLimit || "",
            txnPerDay: binDataSelected?.txnPerDay || "",
            txnPerHour: binDataSelected?.txnPerHour || "",
            posDailyLimit: binDataSelected?.posDailyLimit || "",
            posMonthlyLimit: binDataSelected?.posMonthlyLimit || "",
            onlineDailyLimit: binDataSelected?.onlineDailyLimit || "",
            onlineMonthlyLimit: binDataSelected?.onlineMonthlyLimit || "",
            contactlessDailyLimit: binDataSelected?.contactlessDailyLimit || "",
            contactlessMonthlyLimit: binDataSelected?.contactlessMonthlyLimit || "",
            atmDailyLimit: binDataSelected?.atmDailyLimit || "",
            atmMonthlyLimit: binDataSelected?.atmMonthlyLimit || "",
            atmTransactionLimit: binDataSelected?.atmTransactionLimit || "",
            posTransactionLimit: binDataSelected?.posTransactionLimit || "",
            onlineTransactionLimit: binDataSelected?.onlineTransactionLimit || "",
            contactlessTransactionLimit: binDataSelected?.contactlessTransactionLimit || "",
            expiryDate: binDataSelected?.expiryDate || "",
            allowedTransactions: binDataSelected?.allowedTransactions || defaultAllowedTransactions,
            singleCardUsage: binDataSelected?.singleCardUsage || false,
            allowCNP: binDataSelected?.allowCNP || false,
            cnpChannels: binDataSelected?.cnpChannels || "Web",
            recurringPaymentsUpto: binDataSelected?.recurringPaymentsUpto || "",
            emiDuration: binDataSelected?.emiDuration || [],
            cardAdvance: binDataSelected?.cardAdvance || false,
            cashAdvanceLimit: { type: binDataSelected?.cashAdvanceLimit?.type || "ABSOLUTE", value: binDataSelected?.cashAdvanceLimit?.value || 0 },
            finalCashAdvanceLimit: binDataSelected?.finalCashAdvanceLimit || 0,
            cashAdvanceDailyCap: binDataSelected?.cashAdvanceDailyCap || 0,
            cashAdvanceFee: binDataSelected?.cashAdvanceFee || 0,
        });
    };

    const [singleCardUsageLimits, setSingleCardUsageLimits] = useState({
        totalSpendLimit: filteredBINs[0]?.totalSpendLimit || "",
        expiryDate: filteredBINs[0]?.expiryDate || "",
    });

    useEffect(() => {
        const binDataSelected = filteredBINs.find((bin) => bin.bin === selectedBIN);
        setSingleCardUsageLimits({
            totalSpendLimit: binDataSelected?.totalSpendLimit || "",
            expiryDate: binDataSelected?.expiryDate || "",
        });
    }, [selectedBIN]);



    const calculateCashAdvanceLimit = (creditLimit) => {
        const { type, value } = limits.cashAdvanceLimit;

        return type === "PERCENT"
            ? (creditLimit * value) / 100
            : value;
    };


    const handleSaveLimits = () => {
        const creditLimit = Number(limits.maxCardLimit || 0);
        const finalCashAdvance = calculateCashAdvanceLimit(creditLimit);

        const payload = {
            ...limits,
            finalCashAdvanceLimit: finalCashAdvance,
        };

        console.log(payload);
        TOAST.success("Limits saved successfully");
    };


    const selectStyle = { width: "100%", padding: "0.5rem", borderRadius: "0.375rem", border: "1px solid #D1D5DB", backgroundColor: "#F9FAFB" };



    const transactionTypes = ["POS", "Online", "ATM", "Contactless"];


    const computeDailyLimit = () => {
        let total = 0;
        if (limits.allowedTransactions.ATM) total += Number(limits.atmDailyLimit || 0);
        if (limits.allowedTransactions.POS) total += Number(limits.posDailyLimit || 0);
        if (limits.allowedTransactions.Online) total += Number(limits.onlineDailyLimit || 0);
        if (limits.allowedTransactions.Contactless) total += Number(limits.contactlessDailyLimit || 0);
        return total;
    };

    const computeMonthlyLimit = () => {
        let total = 0;
        if (limits.allowedTransactions.ATM) total += Number(limits.atmMonthlyLimit || 0);
        if (limits.allowedTransactions.POS) total += Number(limits.posMonthlyLimit || 0);
        if (limits.allowedTransactions.Online) total += Number(limits.onlineMonthlyLimit || 0);
        if (limits.allowedTransactions.Contactless) total += Number(limits.contactlessMonthlyLimit || 0);
        return total;
    };


    console.log(limits);


    return (
        <div>
            {/* BIN Selector */}
            <div
                style={{
                    backgroundColor: "#fff",
                    borderRadius: 8,
                    border: "1px solid #E5E7EB",
                    padding: 24,
                    marginBottom: 24
                }}
            >
                <Label style={{ fontSize: 16, fontWeight: 600 }}>
                    Select BIN from BINs mapped to the client
                    <span style={{ color: "#DC2626" }}>*</span>
                </Label>

                <select
                    value={selectedBIN}
                    onChange={(e) => handleBINChange(e.target.value)}
                    style={{
                        marginTop: 8,
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: 6,
                        border: "1px solid #D1D5DB",
                        fontSize: 14,
                        backgroundColor: "#fff",
                        cursor: "pointer",
                    }}
                >
                    {filteredBINs.map((bin) => (
                        <option key={bin.id} value={bin.bin}>
                            {bin.bin} - {bin.brand} - {bin.cardType} - {bin.fundingType}
                        </option>
                    ))}
                </select>
            </div>

            {selectedBIN && !limits.singleCardUsage && (
                <>
                    {/* Card Limits */}
                    <div
                        style={{
                            backgroundColor: "#FFFFFF",
                            borderRadius: 8,
                            border: "1px solid #E5E7EB",
                            padding: 24,
                            marginBottom: 24
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 24
                            }}
                        >
                            <div>
                                <h3 style={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>
                                    Card Limits for {selectedBIN}
                                </h3>
                                <p style={{ fontSize: 14, color: "#4B5563", marginTop: 4 }}>
                                    Maximum limits that can be assigned to individual cards
                                </p>
                            </div>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                            {/* Standard limits */}
                            {[
                                { key: "maxCardLimit", title: "Maximum Card Limit", desc: "Highest credit/spending limit", bg: "#F5F3FF", border: "#DDD6FE" },
                                { key: "dailyLimit", title: "Daily Spend Cap", desc: "Maximum spend per day", bg: "#EFF6FF", border: "#BFDBFE" },
                                { key: "monthlyLimit", title: "Monthly Spend Cap", desc: "Maximum spend per month", bg: "#ECFDF5", border: "#A7F3D0" },
                                { key: "transactionLimit", title: "Amount Per Transaction", desc: "Maximum amount per single transaction", bg: "#FFFBEB", border: "#FDE68A" },
                                { key: "expiryDate", title: "Expiration Year", desc: "Expiration year of the card", bg: "#F5F3FF", border: "#DDD6FE", type: "year" },
                            ].map((item) => (
                                <div key={item.key} style={{ backgroundColor: item.bg, borderRadius: 8, border: `1px solid ${item.border}`, padding: 16 }}>
                                    <Label style={{ fontSize: 14, fontWeight: 600 }}>{item.title} <span style={{ color: "#DC2626" }}>*</span></Label>
                                    <p style={{ fontSize: 12, color: "#4B5563", marginBottom: 8 }}>{item.desc}</p>

                                    {item.key === "expiryDate" ? (
                                        <select
                                            value={limits.expiryDate || ""}
                                            onChange={(e) =>
                                                setLimits({ ...limits, expiryDate: e.target.value })
                                            }
                                            style={{
                                                width: "100%",
                                                padding: "10px 12px",
                                                borderRadius: 6,
                                                border: "1px solid #D1D5DB",
                                                fontSize: 14,
                                                backgroundColor: "#fff",
                                                cursor: "pointer"
                                            }}
                                        >
                                            <option value="" disabled>Select Year</option>
                                            {Array.from({ length: 15 }, (_, i) => new Date().getFullYear() + i).map(
                                                (year) => (
                                                    <option key={year} value={year}>{year}</option>
                                                )
                                            )}
                                        </select>
                                    ) : (
                                        <Input
                                            type="number"
                                            value={limits[item.key]}
                                            onChange={(e) =>
                                                setLimits({ ...limits, [item.key]: Number(e.target.value) })
                                            }
                                            style={{ paddingLeft: 12 }}
                                        />
                                    )}
                                </div>
                            ))}

                            {/* Allowed Transactions */}
                            <div style={{ backgroundColor: "#F0FDF4", borderRadius: 8, border: "1px solid #A7F3D0", padding: 16, gridColumn: "1 / -1" }}>
                                <Label style={{ fontSize: 14, fontWeight: 600 }}>Allowed Transactions</Label>
                                <p style={{ fontSize: 12, color: "#4B5563", marginBottom: 8 }}>
                                    Select transaction types that are allowed
                                </p>

                                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                                    {transactionTypes.map((type) => (
                                        <label key={type} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                            <input
                                                type="checkbox"
                                                checked={limits.allowedTransactions[type]}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;

                                                    setLimits((prev) => {
                                                        const newAllowed = {
                                                            ...prev.allowedTransactions,
                                                            [type]: checked,
                                                        };

                                                        return {
                                                            ...prev,
                                                            allowedTransactions: newAllowed,
                                                            ...(type === "ATM" && !checked
                                                                ? { atmDailyLimit: 0, atmMonthlyLimit: 0, atmTransactionLimit: 0 }
                                                                : {}),
                                                        };
                                                    });
                                                }}
                                            />
                                            {type}
                                        </label>
                                    ))}
                                </div>

                                {/* ATM Daily/Monthly Limits if ATM selected */}
                                {limits.allowedTransactions.ATM && (
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24, marginTop: 16 }}>
                                        <LimitSlider
                                            label="ATM Daily Limit"
                                            value={limits.atmDailyLimit}
                                            max={Number(limits.dailyLimit || 1000000)}
                                            onChange={(val) =>
                                                setLimits({ ...limits, atmDailyLimit: val })
                                            }
                                        />

                                        <LimitSlider
                                            label="ATM Monthly Limit"
                                            value={limits.atmMonthlyLimit}
                                            max={Number(limits.monthlyLimit || 1000000)}
                                            onChange={(val) =>
                                                setLimits({ ...limits, atmMonthlyLimit: val })
                                            }
                                        />

                                        <LimitSlider
                                            label="ATM Per Transaction Limit"
                                            value={limits.atmTransactionLimit}
                                            max={limits.atmDailyLimit || 0}
                                            onChange={(val) =>
                                                setLimits({ ...limits, atmTransactionLimit: val })
                                            }
                                        />
                                    </div>
                                )}


                                {limits.allowedTransactions.POS && (
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24, marginTop: 16 }}>
                                        <LimitSlider
                                            label="POS Daily Limit"
                                            value={limits.posDailyLimit}
                                            max={Number(limits.dailyLimit || 1000000)}
                                            onChange={(val) =>
                                                setLimits({ ...limits, posDailyLimit: val })
                                            }
                                        />

                                        <LimitSlider
                                            label="POS Monthly Limit"
                                            value={limits.posMonthlyLimit}
                                            max={Number(limits.monthlyLimit || 1000000)}
                                            onChange={(val) =>
                                                setLimits({ ...limits, posMonthlyLimit: val })
                                            }
                                        />

                                        <LimitSlider
                                            label="POS Per Transaction Limit"
                                            value={limits.posTransactionLimit}
                                            max={limits.posDailyLimit || 0}
                                            onChange={(val) =>
                                                setLimits({ ...limits, posTransactionLimit: val })
                                            }
                                        />
                                    </div>
                                )}

                                {limits.allowedTransactions.Online && (
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24, marginTop: 16 }}>
                                        <LimitSlider
                                            label="Online Daily Limit"
                                            value={limits.onlineDailyLimit}
                                            max={Number(limits.dailyLimit || 1000000)}
                                            onChange={(val) =>
                                                setLimits({ ...limits, onlineDailyLimit: val })
                                            }
                                        />

                                        <LimitSlider
                                            label="Online Monthly Limit"
                                            value={limits.onlineMonthlyLimit}
                                            max={Number(limits.monthlyLimit || 1000000)}
                                            onChange={(val) =>
                                                setLimits({ ...limits, onlineMonthlyLimit: val })
                                            }
                                        />
                                        <LimitSlider
                                            label="Online Per Transaction Limit"
                                            value={limits.onlineTransactionLimit}
                                            max={limits.onlineDailyLimit || 0}
                                            onChange={(val) =>
                                                setLimits({ ...limits, onlineTransactionLimit: val })
                                            }
                                        />
                                    </div>
                                )}


                                {limits.allowedTransactions.Contactless && (
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24, marginTop: 16 }}>
                                        <LimitSlider
                                            label="Contactless Daily Limit"
                                            value={limits.contactlessDailyLimit}
                                            max={Number(limits.dailyLimit || 1000000)}
                                            onChange={(val) =>
                                                setLimits({ ...limits, contactlessDailyLimit: val })
                                            }
                                        />

                                        <LimitSlider
                                            label="Contactless Monthly Limit"
                                            value={limits.contactlessMonthlyLimit}
                                            max={Number(limits.monthlyLimit || 1000000)}
                                            onChange={(val) =>
                                                setLimits({ ...limits, contactlessMonthlyLimit: val })
                                            }
                                        />

                                        <LimitSlider
                                            label="Contactless Per Transaction Limit"
                                            value={limits.contactlessTransactionLimit}
                                            max={limits.contactlessDailyLimit || 0}
                                            onChange={(val) =>
                                                setLimits({ ...limits, contactlessTransactionLimit: val })
                                            }
                                        />
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>

                    <div style={{ backgroundColor: "#FFFFFF", borderRadius: 8, border: "1px solid #E5E7EB", padding: 24, marginBottom: 24 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                            <div>
                                <h3 style={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>
                                    EMI Rules
                                </h3>
                                <p style={{ fontSize: 14, color: "#4B5563", marginTop: 4 }}>
                                    EMI rules for the card.
                                </p>
                            </div>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 16 }}>
                            <div style={{ backgroundColor: "#FEF2F2", borderRadius: 8, border: "1px solid #FECACA", padding: 12 }}>
                                <Label style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                                    EMI Duration
                                </Label>

                                <div
                                    style={{
                                        border: "1px solid #D1D5DB",
                                        borderRadius: 6,
                                        padding: 8,
                                        background: "#fff",
                                        display: "flex",
                                        gap: 10,
                                        flexWrap: "wrap", // wrap to next line if screen small
                                    }}
                                >
                                    {emiOptions.map((opt) => {
                                        const selected = limits.emiDuration.includes(opt.value);

                                        return (
                                            <label
                                                key={opt.value}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 6,
                                                    padding: "6px 8px",
                                                    borderRadius: 4,
                                                    cursor: "pointer",
                                                    background: selected ? "#E0F2FE" : "transparent",
                                                    border: "1px solid #D1D5DB",
                                                    minWidth: 110,
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selected}
                                                    onChange={() => toggleEmiDuration(opt.value)}
                                                />
                                                <span style={{ fontSize: 14 }}>{opt.label}</span>
                                            </label>
                                        );
                                    })}

                                </div>
                            </div>
                            <div style={{ backgroundColor: "#F5F3FF", borderRadius: 8, border: "1px solid #DDD6FE", padding: 12 }}>
                                <p style={{ fontSize: 14, color: "#4B5563", marginTop: 4 }}>Note: EMI duration can be configured per card product. According to your LOS/LMS, this can be configured and managed centrally.</p>

                            </div>
                        </div>

                    </div>

                    {(limits?.cardAdvance === true) && <div style={{ backgroundColor: "#FFFFFF", borderRadius: 8, border: "1px solid #E5E7EB", padding: 24, marginBottom: 24 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                            <div>
                                <h3 style={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>
                                    Card Advance Rules
                                </h3>
                                <p style={{ fontSize: 14, color: "#4B5563", marginTop: 4 }}>
                                    Card advance rules for the card.
                                </p>
                            </div>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 24, marginTop: 16 }}>
                            <div
                                style={{
                                    backgroundColor: "#FEF2F2",
                                    borderRadius: 8,
                                    border: "1px solid #FECACA",
                                    padding: 12,
                                }}
                            >
                                <Label style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                                    Cash Advance Limit (Absolute / % of Credit Limit)
                                </Label>
                                <div style={{ display: "flex", gap: 10 }}>
                                    {/* Type selector */}
                                    <select
                                        value={limits.cashAdvanceLimit?.type || "ABSOLUTE"}
                                        onChange={(e) => {
                                            const newType = e.target.value;

                                            // If switching to PERCENT, cap value to 100
                                            let newValue = limits.cashAdvanceLimit?.value || 0;
                                            if (newType === "PERCENT" && newValue > 100) newValue = 100;

                                            setLimits({
                                                ...limits,
                                                cashAdvanceLimit: {
                                                    type: newType,
                                                    value: newValue,
                                                },
                                            });
                                        }}
                                        style={{
                                            width: "100%",
                                            padding: "10px 12px",
                                            borderRadius: 6,
                                            border: "1px solid #D1D5DB",
                                            fontSize: 14,
                                            backgroundColor: "#fff",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <option value="ABSOLUTE">Absolute Amount</option>
                                        <option value="PERCENT">% of Credit Limit</option>
                                    </select>

                                    {/* Value input */}
                                    <Input
                                        type="number"
                                        value={limits.cashAdvanceLimit?.value || 0}
                                        onChange={(e) => {
                                            let val = Number(e.target.value);

                                            // Apply dynamic limit if type is PERCENT
                                            if (limits.cashAdvanceLimit?.type === "PERCENT") {
                                                if (val < 0) val = 0;
                                                if (val > 100) val = 100;
                                            }

                                            setLimits({
                                                ...limits,
                                                cashAdvanceLimit: {
                                                    ...limits.cashAdvanceLimit,
                                                    value: val,
                                                },
                                            });
                                        }}
                                        style={{ paddingLeft: 12 }}
                                        placeholder={
                                            limits.cashAdvanceLimit?.type === "PERCENT"
                                                ? "Enter percentage (0–100)"
                                                : "Enter amount"
                                        }
                                    />
                                </div>


                                {/* Helper text */}
                                <div style={{ fontSize: 12, color: "#7F1D1D", marginTop: 6 }}>
                                    {limits.cashAdvanceLimit?.type === "PERCENT"
                                        ? "Value will be calculated as a percentage of credit limit"
                                        : "Value represents a fixed cash advance limit"}
                                </div>
                            </div>



                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24, marginTop: 16 }}>
                            {/* Max Cash Advance per Transaction */}
                            <div style={{ backgroundColor: "#FEF2F2", borderRadius: 8, border: "1px solid #FECACA", padding: 12 }}>
                                <Label style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Max Cash Advance per Transaction</Label>
                                <Input
                                    type="number"
                                    value={calculateCashAdvanceLimit(Number(limits.maxCardLimit || 0))}
                                    readOnly
                                    style={{ paddingLeft: 12 }}
                                />
                                <div style={{ fontSize: 12, color: "#7F1D1D", marginTop: 6 }}>
                                    {limits.cashAdvanceLimit?.type === "PERCENT"
                                        ? `Calculated as ${limits.cashAdvanceLimit.value}% of credit limit`
                                        : "Fixed cash advance limit"}
                                </div>
                            </div>

                            {/* Daily Cash Advance Cap */}
                            <div style={{ backgroundColor: "#FEF2F2", borderRadius: 8, border: "1px solid #FECACA", padding: 12 }}>
                                <Label style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Daily Cash Advance Cap</Label>
                                <Input
                                    type="number"
                                    value={limits.cashAdvanceDailyCap}
                                    onChange={(e) =>
                                        setLimits({ ...limits, cashAdvanceDailyCap: Number(e.target.value) })
                                    }
                                    style={{ paddingLeft: 12 }}
                                    placeholder="Enter daily cash advance limit"
                                />
                                <div style={{ fontSize: 12, color: "#7F1D1D", marginTop: 6 }}>Maximum total cash advance per day</div>
                            </div>

                            {/* Cash Advance Fee (display-only) */}
                            <div style={{ backgroundColor: "#FEF2F2", borderRadius: 8, border: "1px solid #FECACA", padding: 12 }}>
                                <Label style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Cash Advance Fee</Label>
                                <Input
                                    type="number"
                                    value={limits.cashAdvanceFee}
                                    readOnly
                                    style={{ paddingLeft: 12 }}
                                />
                                <div style={{ fontSize: 12, color: "#7F1D1D", marginTop: 6 }}>Bank-defined fee (display only)</div>
                            </div>
                        </div>


                    </div>}


                    {/* Velocity Rules */}
                    <div style={{ backgroundColor: "#FFFFFF", borderRadius: 8, border: "1px solid #E5E7EB", padding: 24, marginBottom: 24 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                            <div>
                                <h3 style={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>
                                    Velocity Rules (Optional)
                                </h3>
                                <p style={{ fontSize: 14, color: "#4B5563", marginTop: 4 }}>
                                    Transaction frequency and volume controls
                                </p>
                            </div>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                            <div style={{ backgroundColor: "#FDF2F8", borderRadius: 8, border: "1px solid #FBCFE8", padding: 16 }}>
                                <Label style={{ fontSize: 14, fontWeight: 600 }}>Transactions Per Day <span style={{ color: "#DC2626" }}>*</span></Label>
                                <p style={{ fontSize: 12, color: "#4B5563", marginBottom: 8 }}>
                                    Optional: Max transactions per day
                                </p>
                                <Input
                                    type="number"
                                    value={limits.txnPerDay}
                                    onChange={(e) =>
                                        setLimits({ ...limits, txnPerDay: Number(e.target.value) })
                                    }
                                />
                            </div>

                            <div style={{ backgroundColor: "#EEF2FF", borderRadius: 8, border: "1px solid #C7D2FE", padding: 16 }}>
                                <Label style={{ fontSize: 14, fontWeight: 600 }}>Transactions Per Hour</Label>
                                <p style={{ fontSize: 12, color: "#4B5563", marginBottom: 8 }}>
                                    Optional: Max transactions per hour
                                </p>
                                <Input
                                    type="number"
                                    value={limits.txnPerHour}
                                    onChange={(e) =>
                                        setLimits({ ...limits, txnPerHour: Number(e.target.value) })
                                    }
                                    placeholder="Leave empty for no limit"
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ backgroundColor: "#FFFFFF", borderRadius: 8, border: "1px solid #E5E7EB", padding: 24, marginBottom: 24 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                            <div>
                                <h3 style={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>
                                    Card Not Present Rules
                                </h3>
                                <p style={{ fontSize: 14, color: "#4B5563", marginTop: 4 }}>
                                    Card not present rules for the card.
                                </p>
                            </div>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24 }}>
                            <div>
                                <Label style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Allow CNP (Card Not Present)</Label>

                                <Switch state={limits.allowCNP} onChange={(value) => setLimits({ ...limits, allowCNP: value })} id="allowCNP" />
                                <Label htmlFor="allowCNP" style={{ fontWeight: 400 }}>
                                    {limits.allowCNP ? true : false}
                                </Label>
                            </div>
                            {limits.allowCNP && (
                                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    <Label style={{ fontSize: 14, fontWeight: 600 }}>
                                        CNP Transaction Channels
                                    </Label>

                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "24px",
                                        }}
                                    >
                                        {["Web", "App", "Recurring Payments"].map((channel) => (
                                            <label
                                                key={channel}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "6px",
                                                    fontSize: 13,
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={limits.cnpChannels.includes(channel)}
                                                    onChange={() =>
                                                        setLimits((prev) => ({
                                                            ...prev,
                                                            cnpChannels: prev.cnpChannels.includes(channel)
                                                                ? prev.cnpChannels.filter((c) => c !== channel)
                                                                : [...prev.cnpChannels, channel],
                                                        }))
                                                    }
                                                />
                                                {channel}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}


                            {
                                limits.cnpChannels.includes("Recurring Payments") && limits.allowCNP && (
                                    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                                        <Label style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Recurring Payments Upto (years) <span style={{ color: "#DC2626" }}>*</span></Label>
                                        <select
                                            value={limits.recurringPaymentsUpto || ""}
                                            onChange={(e) =>
                                                setLimits({ ...limits, recurringPaymentsUpto: e.target.value })
                                            }
                                            style={selectStyle}
                                        >
                                            <option value="" disabled>Select Year</option>
                                            {Array.from({ length: 15 }, (_, i) => new Date().getFullYear() + i).map(
                                                (year) => (
                                                    <option key={year} value={year}>{year}</option>
                                                )
                                            )}
                                        </select>
                                    </div>
                                )
                            }
                        </div>
                    </div>

                    {/* Validation Warnings */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        <div style={{ backgroundColor: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: 12, display: "flex", gap: 8 }}>
                            <AlertTriangle style={{ width: 16, height: 16, color: "#D97706" }} />
                            <div style={{ fontSize: 14, color: "#92400E" }}>
                                <strong>Validation:</strong> Daily spend cap cannot exceed maximum card limit
                            </div>
                        </div>

                        <div style={{ backgroundColor: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: 12, display: "flex", gap: 8 }}>
                            <AlertTriangle style={{ width: 16, height: 16, color: "#DC2626" }} />
                            <div style={{ fontSize: 14, color: "#7F1D1D" }}>
                                <strong>Critical:</strong> Once set, these limits cannot be exceeded by corporates or cardholders.
                            </div>
                        </div>
                    </div>

                    {/* Limit Summary */}
                    <div style={{ backgroundColor: "#F9FAFB", borderRadius: 8, border: "1px solid #E5E7EB", padding: 24, marginTop: 24 }}>
                        <h3 style={{ fontSize: 16, fontWeight: 600, color: "#111827", marginBottom: 16 }}>
                            Limit Summary
                        </h3>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                            <div>
                                <div style={{ fontSize: 12, color: "#4B5563" }}>Per Transaction</div>
                                <div style={{ fontSize: 18, fontWeight: 600 }}>AED {Object.values(limits.allowedTransactions).some(Boolean) ? limits.transactionLimit : 0}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 12, color: "#4B5563" }}>Daily Cap</div>
                                <div style={{ fontSize: 18, fontWeight: 600 }}>AED {computeDailyLimit()}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 12, color: "#4B5563" }}>Monthly Cap</div>
                                <div style={{ fontSize: 18, fontWeight: 600 }}>AED {computeMonthlyLimit()}</div>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 24 }}>
                        <Button variant="outline">Cancel</Button>
                        <Button style={{ backgroundColor: "#7C3AED", color: "#fff" }} onClick={handleSaveLimits}>
                            Save Limit Configuration
                        </Button>
                    </div>
                </>
            )}

            {selectedBIN && limits.singleCardUsage && (
                <div>
                    <h3>Single Card Usage</h3>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                        <div style={{ backgroundColor: "#F5F3FF", borderRadius: 8, border: "1px solid #DDD6FE", padding: 16, marginTop: 12 }}>
                            <Label style={{ fontSize: 14, fontWeight: 600 }}>Total Spend Limit <span style={{ color: "#DC2626" }}>*</span></Label>
                            <p style={{ fontSize: 12, color: "#4B5563", marginBottom: 8 }}>
                                Total spend limit for the card. If the total spend limit is reached, the card will expire.
                            </p>
                            <Input
                                type="number"
                                value={singleCardUsageLimits.totalSpendLimit}
                                onChange={(e) => setSingleCardUsageLimits({ ...singleCardUsageLimits, totalSpendLimit: Number(e.target.value) })}
                            />
                        </div>
                        <div style={{ backgroundColor: "#EEF2FF", borderRadius: 8, border: "1px solid #DDD6FE", padding: 16, marginTop: 12 }}>
                            <Label style={{ fontSize: 14, fontWeight: 600 }}>Expiry Date <span style={{ color: "#DC2626" }}>*</span></Label>
                            <p style={{ fontSize: 12, color: "#4B5563", marginBottom: 8 }}>
                                Expiry date for the card. If the expiry date is reached, the card will expire.
                            </p>
                            <select
                                value={singleCardUsageLimits.expiryDate || ""}
                                onChange={(e) =>
                                    setSingleCardUsageLimits({ ...singleCardUsageLimits, expiryDate: e.target.value })
                                }
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    borderRadius: 6,
                                    border: "1px solid #D1D5DB",
                                    fontSize: 14,
                                    backgroundColor: "#fff",
                                    cursor: "pointer"
                                }}
                            >
                                <option value="" disabled>Select Year</option>
                                {Array.from({ length: 15 }, (_, i) => new Date().getFullYear() + i).map(
                                    (year) => (
                                        <option key={year} value={year}>{year}</option>
                                    )
                                )}
                            </select>
                        </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 24 }}>
                        <Button variant="outline">Cancel</Button>
                        <Button style={{ backgroundColor: "#7C3AED", color: "#fff" }} onClick={handleSaveLimits}>
                            Save Limit Configuration
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CardLimits;
